// ═══ 정산서 보관함 (리빌드 R3) — "발행 = 카톡 읽음 표시 감각" ═══
// 정본 UX: zero-base-design.md §6.4. 데이터: GET /statements?agencyId= (+accountId)
//
// props:
//   user · showToast · onBack (정산 탭 복귀) · focusAccountId (선택 — 특정 계정 필터로 진입)
//
// 동작 계약:
//  - 행: 기간 · 계정명 · 미수(closing_balance) · 발행일 · 상태 뱃지들:
//    보냄(sent_at) / 재발행 필요(changedAfterIssue) / 구판(superseded_by>0 흐림)
//    ※ 열람 횟수는 표시하지 않는다 (Jin 8/20) — API 의 viewCount 는 계속 오지만 admin 은 쓰지 않는다.
//  - 행 액션: [정산서 보기] — 거래처가 받는 공유 웹페이지를 그대로 연다(?preview=1 = 열람 미기록).
//    파일(요약 PDF / 업체별 ZIP / 단일 PDF)은 그 페이지 안에서 전부 받으므로 별도 다운로드 버튼은 없다.
//    · [공유하기] shareUrl 클립보드 복사
//    + POST /statements/:id/sent · [재발행] confirm 후 POST /billing-accounts/:accountId/statements
//    {period} → 목록 갱신
//    · [💬 카톡 공유] 는 구판 아닌 행에 **상시** 노출 (Jin 9/2 6차 승격).
//      Kakao JS SDK 공유 피커(Share.sendDefault feed)로 실제 전송한다. 카드에 금액은 넣지 않는다
//      (대화목록 미리보기 노출 방지 — Jin 승인 조건). SDK 불가 시 기존 navigator.share →
//      kakaotalk:// 폴백 그대로. 폴백은 앱 미설치 시 no-op 이 정상이라 토스트를 띄우지 않는다.
//      ⚠️ sendDefault 는 클릭 핸들러에서 **동기** 호출 — 그 다음에 sent 기록을 fire-and-forget.
//         순서를 뒤집으면(await 후 호출) 사파리가 팝업을 막는다 (utils/kakaoShare.js 헤더).
//  - 에러/빈 상태 가드.
import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { useIsMobile } from '../utils/useMobile';
import { ensureKakao, shareStatementLink, periodTitle } from '../utils/kakaoShare';

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
// 'YYYY-MM' → 'YYYY년 M월' (월 선택 UI 와 동일 감각 — '마감' 용어는 노출하지 않는다)
var periodLabel = function (p) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(p || ''));
  return m ? m[1] + '년 ' + parseInt(m[2], 10) + '월' : (p || '-');
};
var dateLabel = function (v) {
  if (!v) return '-';
  var s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

var thL = { padding: '8px 12px', fontSize: 11, fontWeight: 700, textAlign: 'left' };
var thR = { padding: '8px 12px', fontSize: 11, fontWeight: 700, textAlign: 'right' };
var td = { padding: '8px 12px', color: '#475569' };
var badgeBase = { display: 'inline-block', marginRight: 4, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' };
var badgeSent = Object.assign({}, badgeBase, { background: '#eff6ff', color: '#1d4ed8' });
var badgeChanged = Object.assign({}, badgeBase, { background: '#ffedd5', color: '#9a3412' });
var badgeOld = Object.assign({}, badgeBase, { background: '#f1f5f9', color: '#64748b' });

function CancelAgencyStatements(props) {
  var user = props.user, showToast = props.showToast || function () {};
  var focusAccountId = parseInt(props.focusAccountId, 10) || 0;
  var [rows, setRows] = useState([]);
  var [err, setErr] = useState('');
  var [loaded, setLoaded] = useState(false);
  var [busyId, setBusyId] = useState(0);
  var isMobile = useIsMobile();
  var tap = isMobile ? { minHeight: 40 } : undefined; // 모바일 터치 타깃

  var agencyId = (user && user.agencyId) || 0;

  var load = function () {
    var qs = focusAccountId ? ('?accountId=' + focusAccountId) : ('?agencyId=' + (agencyId || ''));
    setLoaded(false); setErr('');
    authFetch(API + '/statements' + qs).then(function (r) { return r.json(); }).then(function (d) {
      // 에러 응답(401/403/400)은 배열이 아니다 — map 크래시 가드
      if (!Array.isArray(d)) { setRows([]); setErr((d && d.error) || '조회 실패'); setLoaded(true); return; }
      setRows(d); setLoaded(true);
    }).catch(function () { setRows([]); setErr('조회 실패'); setLoaded(true); });
  };
  useEffect(load, [focusAccountId, agencyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // [정산서 보기] — 거래처가 받는 공유 웹페이지를 그대로 연다 (토큰 링크라 인증 불필요).
  // 다운로드는 그 페이지 안에서 전부 된다(요약 PDF / 업체별 ZIP / 업체 단일 PDF) — admin 버튼은 중복이라 뺐다.
  // ?preview=1 — 발행자 본인의 확인 열람은 서버가 viewCount 에 기록하지 않는다
  // (열람 수 = "거래처가 공유 링크를 연 횟수" 지표 보존, routes/statements.js 참조).
  var openStatementPage = function (st) {
    var url = st.shareUrl || '';
    if (!url) { showToast('공유 링크가 없습니다', 'err'); return; }
    window.open(url + (url.indexOf('?') >= 0 ? '&' : '?') + 'preview=1', '_blank', 'noopener');
  };

  // 클립보드 — 비보안 컨텍스트/미지원 브라우저는 prompt 폴백(복사 자체를 실패시키지 않는다)
  var writeClip = function (url) {
    if (!url) return false;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).catch(function () { window.prompt('공유 링크를 복사하세요', url); });
      return true;
    }
    window.prompt('공유 링크를 복사하세요', url);
    return true;
  };

  // 구(舊) 경로 — 링크를 클립보드에 두고 "붙여넣을 창"만 띄운다. SDK 를 못 쓸 때의 폴백.
  // ① 네이티브 공유 시트(모바일) → ② kakaotalk:// 스킴. 앱 미설치(대개 데스크톱)면 no-op 이 정상이라
  //    감지하지도, 토스트로 알리지도 않는다.
  var openKakaoFallback = function (url) {
    if (!url) return;
    if (navigator.share) {
      try { var p = navigator.share({ text: url }); if (p && p.catch) p.catch(function () {}); return; } catch (e) { /* 폴백 */ }
    }
    try { window.location.href = 'kakaotalk://'; } catch (e) { /* 미설치 — no-op */ }
  };

  // [💬 카톡 공유] — SDK 공유 피커 우선, 불가 시 위 폴백.
  // ⚠️ 순서 고정: sendDefault(동기) 먼저 → sent 기록은 뒤에서 fire-and-forget.
  //    await 뒤로 밀면 사용자 제스처가 끊겨 사파리가 팝업을 막는다.
  var shareKakao = function (st) {
    var url = st.shareUrl || '';
    if (!url) { showToast('공유 링크가 없습니다 — [공유하기] 로 먼저 발급하세요', 'err'); return; }
    var ok = false;
    if (ensureKakao()) {
      ok = shareStatementLink({
        url: url,
        title: periodTitle(st.accountName, st.period),
        // 금액은 절대 넣지 않는다 (대화목록 미리보기 노출 방지)
        description: 'AUTODEX 말소 대행 정산서',
        onError: function (m) { showToast('카카오 공유 실패: ' + m, 'err'); }
      });
    }
    if (!ok) { writeClip(url); openKakaoFallback(url); }
    // 보냄 기록 — 공유 창을 띄운 뒤에. 이미 기록된 행은 다시 부르지 않는다(중복 무해하나 무의미).
    if (!st.sent_at) {
      authFetch(API + '/statements/' + st.id + '/sent', { method: 'POST' })
        .then(function (r) { return r.json(); })
        .then(function (d) { if (d && d.success) load(); })
        .catch(function () { /* 공유는 이미 떴다 — 기록 실패로 사용자를 막지 않는다 */ });
    }
  };

  // [공유하기] — 링크 복사 + 발송(보냄) 기록. 목록에 shareUrl 이 없으면 sent 응답의 shareUrl 로 폴백.
  // 링크만 필요한 경우(메일·문자·직접 붙여넣기)를 위해 카톡 공유와 별개로 유지한다.
  var share = function (st) {
    var pre = st.shareUrl || '';
    if (pre) writeClip(pre);
    setBusyId(st.id);
    authFetch(API + '/statements/' + st.id + '/sent', { method: 'POST' }).then(function (r) { return r.json(); }).then(function (d) {
      setBusyId(0);
      if (!d || !d.success) { showToast((d && d.error) || '발송 기록 실패', 'err'); return; }
      if (!pre) writeClip(d.shareUrl || '');
      showToast('공유 링크 복사됨 — 카톡에 붙여넣으세요');
      load();
    }).catch(function () { setBusyId(0); showToast('서버 오류', 'err'); });
  };

  // [재발행] — 신판 발행 + 구판 superseded 처리(서버). 스냅샷은 고치지 않는다.
  var reissue = function (st) {
    if (!st.accountId) { showToast('계정 정보가 없어 재발행할 수 없습니다', 'err'); return; }
    if (!window.confirm(periodLabel(st.period) + ' ' + (st.accountName || '') + ' 정산서를 재발행할까요?\n새 정산서가 발행되고 기존 정산서는 구판이 됩니다.')) return;
    setBusyId(st.id);
    authFetch(API + '/billing-accounts/' + st.accountId + '/statements', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period: st.period })
    }).then(function (r) { return r.json(); }).then(function (d) {
      setBusyId(0);
      if (!d || !d.success) { showToast((d && d.error) || '재발행 실패', 'err'); return; }
      showToast('재발행 완료 (미수 ' + fmt(d.closingBalance) + '원)');
      load();
    }).catch(function () { setBusyId(0); showToast('서버 오류', 'err'); });
  };

  // 상태 뱃지 · 행 액션 — 데스크탑 표와 모바일 카드가 같은 것을 쓴다 (표기/동작 단일 원천).
  var statusBadges = function (st, old) {
    return (<>
      {!!st.sent_at && <span style={badgeSent}>보냄</span>}
      {!!st.changedAfterIssue && !old && <span style={badgeChanged}>재발행 필요</span>}
      {!!old && <span style={badgeOld}>구판</span>}
      {!st.sent_at && !st.changedAfterIssue && !old && <span style={{ color: '#94a3b8', fontSize: 11 }}>발행됨</span>}
    </>);
  };
  // 모바일 카드에서는 버튼이 카드 폭을 3등분한다 (표에서는 우측 정렬 인라인) — extra 로 그 차이만 주입.
  var rowActions = function (st, old, busy, extra) {
    return (<>
      {/* 정산서 보기 — 거래처가 보는 그 화면(공유 웹페이지). 파일은 그 안에서 받는다 (Jin 8/20) */}
      {!!st.shareUrl && <button className="btn btn-outline btn-sm" style={Object.assign({}, tap, extra)} disabled={busy}
        onClick={function () { openStatementPage(st); }}>정산서 보기</button>}
      {/* 구판은 보낼 대상이 아니다 — [정산서 보기]만 남긴다 (링크는 소급 유효). */}
      {!old && <button className="btn btn-outline btn-sm" style={Object.assign({ marginLeft: 4 }, tap, extra)} disabled={busy} onClick={function () { share(st); }}>공유하기</button>}
      {/* 복사가 끝난 직후에만 — 링크는 이미 클립보드에 있고 이 버튼은 붙여넣을 창을 띄울 뿐이다 */}
      {!old && !!st.shareUrl && <button className="btn btn-outline btn-sm"
        style={Object.assign({ marginLeft: 4 }, tap, extra)} onClick={function () { shareKakao(st); }}>💬 카톡 공유</button>}
      {!old && <button className="btn btn-outline btn-sm" style={Object.assign({ marginLeft: 4 }, tap, extra)} disabled={busy} onClick={function () { reissue(st); }}>재발행</button>}
    </>);
  };
  // 모바일 카드 액션 — marginLeft 대신 gap, 각 버튼이 폭을 나눠 갖는다
  var mAct = { marginLeft: 0, flex: 1, justifyContent: 'center' };

  return (<div>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
      {!!props.onBack && <button className="btn btn-outline btn-sm" onClick={props.onBack}>← 정산</button>}
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e3a5f' }}>정산서 보관함</h3>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>발행한 정산서는 그대로 보존됩니다 — 명세가 바뀌면 재발행하세요.</span>
      <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={load}>새로고침</button>
    </div>

    {/* 모바일 — 6열 표는 minWidth 640 가로 스크롤이라 거래처명이 한 글자씩 접히고 액션 버튼은 화면 밖이었다.
        → 카드 스택. 기간·거래처 / 발행일·미수(우측 정렬) / 상태 뱃지 / 액션 3버튼 순. */}
    {!!isMobile && <div>
      {rows.map(function (st) {
        var old = (st.superseded_by || 0) > 0;
        var busy = busyId === st.id;
        return (<div key={st.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 12, marginBottom: 8, opacity: old ? 0.65 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>{periodLabel(st.period)}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.accountName || '-'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>발행 {dateLabel(st.issued_at)}</span>
            <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', color: (st.closing_balance || 0) > 0 ? '#dc2626' : '#059669' }}>미수 {fmt(st.closing_balance)}원</span>
          </div>
          <div style={{ marginTop: 6 }}>{statusBadges(st, old)}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>{rowActions(st, old, busy, mAct)}</div>
        </div>);
      })}
      {rows.length === 0 && <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: '28px 12px', textAlign: 'center', fontSize: 12.5, color: err ? '#dc2626' : '#94a3b8' }}>
        {!loaded ? '불러오는 중…' : (err || '발행된 정산서가 없습니다. 정산 탭에서 [정산서 발행] 을 누르세요.')}
      </div>}
    </div>}

    {!isMobile && <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ background: '#1e3a5f', color: '#fff' }}>
          <th style={thL}>기간</th>
          <th style={thL}>거래처</th>
          <th style={thR}>미수</th>
          <th style={thL}>발행일</th>
          <th style={thL}>상태</th>
          <th style={thR}></th>
        </tr></thead>
        <tbody>
          {rows.map(function (st, i) {
            var old = (st.superseded_by || 0) > 0;
            var busy = busyId === st.id;
            return (<tr key={st.id} style={{ background: i % 2 === 1 ? '#f8fafc' : '#fff', borderBottom: '1px solid #f1f5f9', opacity: old ? 0.55 : 1 }}>
              <td style={Object.assign({ fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }, td)}>{periodLabel(st.period)}</td>
              <td style={Object.assign({ color: '#1e293b' }, td)}>{st.accountName || '-'}</td>
              <td style={Object.assign({ textAlign: 'right', fontWeight: 700, color: (st.closing_balance || 0) > 0 ? '#dc2626' : '#059669' }, td)}>{fmt(st.closing_balance)}</td>
              <td style={Object.assign({ whiteSpace: 'nowrap' }, td)}>{dateLabel(st.issued_at)}</td>
              <td style={td}>{statusBadges(st, old)}</td>
              <td style={Object.assign({ textAlign: 'right', whiteSpace: 'nowrap' }, td)}>{rowActions(st, old, busy)}</td>
            </tr>);
          })}
          {rows.length === 0 && <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: err ? '#dc2626' : '#94a3b8' }}>
            {!loaded ? '불러오는 중…' : (err || '발행된 정산서가 없습니다. 정산 탭에서 [정산서 발행] 을 누르세요.')}
          </td></tr>}
        </tbody>
      </table>
    </div>}
    <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>정산서 보기 = 받는 분에게 보이는 공유 페이지 그대로 (요약 PDF · 업체별 ZIP 도 그 안에서 받습니다) · 카톡 공유 = 카카오 공유 피커로 바로 전송 (금액은 카드에 넣지 않습니다) · 공유하기 = 링크 복사 + 보냄 기록 · 재발행 = 최신 명세로 새 정산서 발행(구판 보존)</p>
  </div>);
}

export default CancelAgencyStatements;
